import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RestTimerCircle from './rest-timer-circle';
import { addTimer, deleteTimer, getTimer, timers } from '../../../utilis/helpers';
import { SettingMode, IN_PERFORMANCE_ACTION_TYPES } from '../../../constants/constants';
import { trackEvent } from '../../../services/analytics';
import { getInPerformanceTrackingProps } from '../../../zustand/session-metadata-store';
import BordersAndSettingIcon from '../borders-and-setting-icon';
import 'twin.macro';

const ResetBetweenSets = ({
  currentSet,
  NumOfSets,
  timer,
  rest,
  restInterval,
  sendJsonMessage,
  dispatch,
  timerControl,
}) => {
  const { t } = useTranslation('exercise');
  const RestBetweenSetsTime = useMemo(() => {
    const numericRest = Number(restInterval ?? rest);
    return (Number.isFinite(numericRest) && numericRest > 0 ? numericRest : 5) * 1000;
  }, [rest, restInterval]);

  useEffect(() => {
    addTimer({ key: 'Result', value: timer });
    return () => deleteTimer({ key: 'Result' });
  }, []);

  const completeTime = useMemo(() => {
    if (RestBetweenSetsTime <= 0) {
      return 0;
    }
    return getTimer({
      key: 'Result',
      timer: timer,
      strategy: 'Decrease',
      totalValue: RestBetweenSetsTime,
    });
  }, [RestBetweenSetsTime, timer]);

  const handelSkipRestStage = () => {
    trackEvent('INPERFORMANCE_ACTION', {
      ...getInPerformanceTrackingProps(),
      action_type: IN_PERFORMANCE_ACTION_TYPES.SKIP_REST,
    });
    sendJsonMessage?.({
      pause: {
        action: SettingMode.skipRestart,
      },
    });
  };

  return (
    <div tw="fixed inset-0 z-[100] flex flex-col bg-[rgba(21,21,21,0.8)]">
      <div tw="absolute top-4 right-4 z-[110] flex items-center gap-2 rounded-[12px] bg-black/70 p-2 text-white shadow-[0_12px_30px_rgba(0,0,0,0.24)]">
        <BordersAndSettingIcon
          dispatch={dispatch}
          timer={timerControl}
          sendJsonMessage={sendJsonMessage}
        />
      </div>

      {/* Main content - two panels; stacked on mobile portrait only —
          landscape mobile keeps the side-by-side layout, same as desktop. */}
      <div tw="flex flex-1 overflow-hidden max-lg:[@media(orientation:portrait)]:flex-col max-lg:[@media(orientation:portrait)]:gap-4 max-lg:[@media(orientation:portrait)]:overflow-y-auto max-lg:[@media(orientation:portrait)]:p-4">
        {/* rest message */}
        <div tw="flex flex-1 items-center justify-center p-10 max-lg:[@media(orientation:portrait)]:p-0">
          <div tw="flex w-full flex-col items-center justify-center gap-[9px] rounded-[24px] border border-[#6d737b] bg-[rgba(39,39,39,0.8)] p-10 text-center max-lg:[@media(orientation:portrait)]:gap-3 max-lg:[@media(orientation:portrait)]:rounded-[12px] max-lg:[@media(orientation:portrait)]:p-5">
            <p tw="m-0 xl:text-[64px] text-[42px] font-bold leading-[1.2] tracking-[1.28px] text-white max-lg:[@media(orientation:portrait)]:text-[32px] max-lg:[@media(orientation:portrait)]:tracking-[0.64px]">
              {t('restBetweenSets.takeAQuickRest')}
            </p>
            <p tw="m-0 xl:text-[64px] text-[42px]  font-bold tracking-[0.8px] text-[#ababab] max-lg:[@media(orientation:portrait)]:text-[24px] max-lg:[@media(orientation:portrait)]:tracking-[0.48px]">
              {t('restBetweenSets.setsCompleted', { completed: currentSet, total: NumOfSets })}
            </p>
          </div>
        </div>

        {/* timer */}
        <div tw="flex flex-1 items-center justify-center p-10 max-lg:[@media(orientation:portrait)]:p-0">
          <div tw="flex flex-col items-center justify-center gap-3 rounded-[24px] border border-[#6d737b] bg-[rgba(39,39,39,0.8)] p-10 max-lg:[@media(orientation:portrait)]:w-full max-lg:[@media(orientation:portrait)]:rounded-[12px] max-lg:[@media(orientation:portrait)]:p-5">
            {/* Landscape mobile/tablet drop this label to keep the panel
                compact — desktop and portrait mobile both keep it. */}
            <p tw="m-0 text-center xl:text-[64px] text-[42px]  font-black text-white max-lg:[@media(orientation:landscape)]:hidden max-lg:[@media(orientation:portrait)]:text-[24px]">
              {t('restBetweenSets.restTime')}
            </p>
            <RestTimerCircle
              timer={timers.Result ? completeTime : RestBetweenSetsTime}
              convertOn={RestBetweenSetsTime}
              large
            />
          </div>
        </div>
      </div>

      {/* Bottom CTA bar — full-width button below lg (both portrait and
          landscape mobile/tablet); right-aligned auto-width button on
          desktop (lg and up). */}
      <div
        onClick={handelSkipRestStage}
        tw="flex shrink-0 items-center cursor-pointer justify-center px-4 py-4 gap-3 lg:justify-end lg:px-6 lg:py-[18px]"
      >
        <div tw="flex w-full items-center justify-center gap-2 rounded-[6px] border border-[#8da12b] bg-[#c6d86e] py-3 pl-4 pr-3 text-[15px] font-medium leading-[20px] text-[#2e2e2e] lg:w-auto lg:justify-start">
          <span>{t('restBetweenSets.startNextSet')}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" tw="rtl:scale-x-[-1]">
            <path
              d="M7.5 15L12.5 10L7.5 5"
              stroke="#2e2e2e"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ResetBetweenSets;
